import { Bell, Search, LogOut, User, Settings, AlertTriangle, Shield, FileText, RefreshCw } from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';
import { useLogout } from '@/api/hooks/useAuth';
import { X } from 'lucide-react';
import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useDismissNotification,
  type Notification,
} from '@/api/hooks/useNotifications';
import { resolveNotificationRoute } from '@/lib/notificationRoute';
import { getInitials, cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Icon by store category (NOTIF.3 — priority COLOR is v2; content only here).
const CATEGORY_ICON: Record<string, typeof AlertTriangle> = {
  assessment: FileText,
  compliance: Shield,
  tenant_workflow: AlertTriangle,
  template_version: FileText,
  product_update: Sparkles,
  system_admin: Settings,
};
function notificationIcon(n: Notification) {
  return CATEGORY_ICON[n.category] ?? Bell;
}
const CATEGORY_LABEL: Record<string, string> = {
  assessment: 'Assessment',
  compliance: 'Compliance',
  tenant_workflow: 'Workflow',
  template_version: 'Template',
  product_update: 'Product',
  system_admin: 'System',
};

export function Header() {
  const { user } = useAuthStore();
  const { setSearchOpen } = useUiStore();
  const logout = useLogout();
  const navigate = useNavigate();
  const { data: notifications = [] } = useNotifications();
  const { data: unreadCount = 0 } = useUnreadCount();
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border-subtle bg-bg-surface px-4">
      {/* Breadcrumbs */}
      <div className="flex-1 min-w-0">
        <Breadcrumbs />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Notifications" title="Notifications" className="relative">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent-rose text-[9px] font-bold text-white leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 max-h-[440px] overflow-y-auto">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <span className="text-xs font-normal text-accent-rose">{unreadCount} unread</span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 ? (
              <div className="py-6 text-center text-xs text-text-secondary">No notifications</div>
            ) : (
              notifications.map((n) => {
                const Icon = notificationIcon(n);
                // Click marks the item read (deep-linking deferred to NOTIF.4). Div rows (not
                // DropdownMenuItem) so the menu stays open and the dismiss button nests cleanly.
                return (
                  <div
                    key={n.id}
                    className="flex items-start gap-3 rounded px-2 py-3 cursor-pointer hover:bg-bg-muted"
                    onClick={() => {
                      if (!n.was_read) markRead.mutate(n.id);
                      const route = resolveNotificationRoute(n.resource_type, n.resource_id);
                      if (route) navigate(route); // deep-link (NOTIF.4); unmapped/null → stay (no-op)
                    }}
                  >
                    <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', n.was_read ? 'text-text-secondary' : 'text-text-primary')} />
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <p className={cn('text-xs leading-snug truncate', n.was_read ? 'text-text-secondary' : 'text-text-primary font-medium')}>
                        {n.title}
                      </p>
                      <p className="text-[10px] text-text-secondary">
                        {CATEGORY_LABEL[n.category] ?? n.category} · {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss notification"
                      className="shrink-0 rounded p-0.5 text-text-secondary hover:bg-bg-surface hover:text-text-primary"
                      onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="justify-center text-xs text-text-secondary gap-1.5" onSelect={() => navigate('/notifications')}>
              <RefreshCw className="h-3 w-3" />
              View all
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Search — grouped adjacent to the notification bell (mockup order) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSearchOpen(true)}
          aria-label="Search (Ctrl+K)"
          title="Search (Ctrl+K)"
        >
          <Search className="h-4 w-4" />
        </Button>

        {/* group divider: [bell + search] | [profile] */}
        <span aria-hidden className="mx-1 h-5 w-px bg-border-subtle" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="rounded-full ml-1" aria-label="User menu">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-[10px]">
                  {user ? getInitials(user.displayName) : '?'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-0.5">
                <p className="text-sm font-medium text-text-primary">{user?.tenantName}</p>
                <p className="text-xs text-text-secondary font-mono">{user?.tenantSlug}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-0.5">
                <p className="text-sm font-medium text-text-primary">{user?.displayName}</p>
                <p className="text-xs text-text-secondary truncate">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
              <User />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/settings')}>
              <Settings />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-accent-rose focus:text-accent-rose"
              onSelect={() => logout.mutate()}
            >
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
